// @ts-strict-ignore
import { formatDuration as dateFnsFormatDuration } from "date-fns";
import { enUS } from "date-fns/locale";
import type { Duration, Locale } from "date-fns";

import { getGreatestWholeTimeUnit } from "./time";
import { TimeUnit } from "./types";
import type { FormatDuration, TimeUnitKey } from "./types";
import { dateFnsLocaleMap } from "./dateFnsLocaleMap";

/**
 * Returns the date-fns Locale object for the passed language.
 * Defaults to english if no locale is defined.
 *
 * @param language ISO 639-1 language code.
 */
export const getDateFnsLocaleFromLanguage = (language: string): Locale => {
  return dateFnsLocaleMap[language] ?? enUS;
};

const timeUnitKeyToDateFnsDurationKey: { [K in TimeUnitKey]: keyof Duration } = {
  Second: "seconds",
  Minute: "minutes",
  Hour: "hours",
  Day: "days",
  Week: "weeks",
};

/**
 * @deprecated use `@services/useFormatDuration` instead
 *
 * Returns a string representing the time as a human readable duration.
 *
 * Output can be internationalized by passing in a locale string in the
 * options, like:
 * @example
 *   formatDuration(1, "Second", { language });
 *
 * @note Output is always in the same units as are passed in (e.g. 125 Second becomes
 *   "125 seconds" and not "2 minutes 5 seconds").
 *
 * @param value time value (in seconds if no unit is given)
 * @param timeUnit the optional time unit of the value
 * @param options options object
 */
export const formatDuration: FormatDuration = (value, timeUnit = "Second", options = {}): string => {
  let duration: Duration;

  if (!Number.isInteger(value) || value < 0) {
    value = 0;
  }

  if (timeUnit === "natural") {
    let leftover = value;
    let days = 0,
      hours = 0,
      minutes = 0,
      seconds = 0;

    days = Math.floor(leftover / TimeUnit.Day);
    leftover = leftover - days * TimeUnit.Day;

    hours = Math.floor(leftover / TimeUnit.Hour);
    leftover = leftover - hours * TimeUnit.Hour;

    minutes = Math.floor(leftover / TimeUnit.Minute);
    leftover = leftover - minutes * TimeUnit.Minute;

    seconds = leftover;

    duration = { years: 0, months: 0, days, hours, minutes, seconds };
  } else {
    if (timeUnit === "auto") {
      [value, timeUnit] = getGreatestWholeTimeUnit(value);
    }
    duration = { [timeUnitKeyToDateFnsDurationKey[timeUnit]]: value };
  }

  const locale = getDateFnsLocaleFromLanguage(options.locale);
  return dateFnsFormatDuration(duration, {
    locale,
    zero: value === 0,
    format: value === 0 ? ["seconds"] : undefined,
  });
};
